import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, ShieldCheck } from "lucide-react";
import { rpc, translateError } from "@vixio/data";
import { Button, Callout, Skeleton, ThemeMenu, Wordmark, formatDate } from "@vixio/ui";
import type { PublicSurvey } from "./api";

/**
 * Página pública /pulso/:token (link do convite pulso.convite): o colaborador responde às 5 afirmações, sem login.
 * Anônima por construção no banco (pulso.submit não guarda pessoa, convite, IP nem hora); o resultado da equipe só
 * aparece com o mínimo de respostas (pelo menos 5), imposto no banco. Mobile-first: uma coluna, alvos grandes.
 */
export function PublicSurveyPage() {
  const { token = "" } = useParams();
  const survey = useQuery({ queryKey: ["pulso", "survey", token], queryFn: () => rpc("pulso", "get_survey", { p_token: token }), retry: false });
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const s = survey.data;
  const items = s?.items ?? [];
  const answered = items.filter((i) => answers[i.id] !== undefined).length;
  const complete = items.length > 0 && answered === items.length;

  async function submit() {
    setError(null);
    if (!complete) {
      setError("Responda a todas as afirmações antes de enviar.");
      return;
    }
    setBusy(true);
    try {
      const r = await rpc("pulso", "submit", { p_token: token, p_answers: answers });
      setDone(r.message);
    } catch (e) {
      setError(translateError(e).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="vx-public">
      <header className="vx-public__bar">
        <span className="vx-brand" style={{ border: 0, padding: 0 }}>
          <Wordmark label="Pulso de Clima" />
        </span>
        <span className="vx-spacer" style={{ flex: 1 }} />
        <ThemeMenu />
      </header>
      <main className="vx-public__body" id="conteudo" style={{ maxWidth: 600 }}>
        <div className="vx-stack vx-stack--lg">
          {survey.isLoading ? <Skeleton lines={6} /> : null}
          {survey.error ? (
            <Callout tone="warn" title="Este link não está disponível">
              {translateError(survey.error).message}
            </Callout>
          ) : null}
          {s && done ? (
            <section aria-labelledby="pulso-obrigado" className="vx-stack" style={{ alignItems: "center", textAlign: "center", paddingTop: 24 }}>
              <CheckCircle2 aria-hidden="true" style={{ width: 56, height: 56, color: "var(--ok)" }} />
              <h1 id="pulso-obrigado" style={{ margin: 0 }}>
                Obrigado!
              </h1>
              <p className="text-2" style={{ margin: 0, maxWidth: 440 }}>
                {done}
              </p>
              <p className="text-2" style={{ margin: 0, maxWidth: 440 }}>
                Sua resposta entrou sem nome, e-mail nem horário. Pode fechar esta página.
              </p>
            </section>
          ) : null}
          {s && !done ? (
            <>
              <header className="vx-stack" style={{ gap: 6 }}>
                <p className="text-2" style={{ margin: 0 }}>
                  {[s.organization, `Equipe ${s.team}`, `ciclo ${s.cycle}`].filter(Boolean).join(" · ")}
                </p>
                <h1 style={{ margin: 0 }}>{s.title ?? "Pulso de Clima"}</h1>
                <p className="text-2" style={{ margin: 0 }}>
                  Responda até {formatDate(s.ends_on)}. Leva menos de um minuto.
                </p>
              </header>
              <Callout tone="info" title="Sua resposta é anônima">
                <span style={{ display: "inline-flex", gap: 6, alignItems: "flex-start" }}>
                  <ShieldCheck aria-hidden="true" style={{ width: 18, height: 18, flexShrink: 0, marginTop: 2 }} />
                  <span>
                    Ninguém vê respostas individuais: não guardamos seu nome, e-mail nem o horário. O resultado da equipe só aparece
                    com pelo menos {s.min_responses} respostas.
                  </span>
                </span>
              </Callout>
              {s.intro ? (
                <p style={{ margin: 0 }} className="text-2">
                  {s.intro}
                </p>
              ) : null}
              <form
                className="vx-stack vx-stack--lg"
                onSubmit={(e) => {
                  e.preventDefault();
                  void submit();
                }}
              >
                {items.map((it, n) => (
                  <fieldset key={it.id} className="vx-fieldset" style={{ border: 0, padding: 0, margin: 0 }}>
                    <legend style={{ fontWeight: 600, marginBottom: 10, lineHeight: 1.4 }}>
                      <span className="num text-2">{n + 1}. </span>
                      {it.text}
                    </legend>
                    <div role="radiogroup" aria-label={it.text} style={{ display: "grid", gap: 8 }}>
                      {(s.scale?.labels ?? []).map((label, k) => {
                        const value = (s.scale?.min ?? 1) + k;
                        const checked = answers[it.id] === value;
                        return (
                          <label
                            key={value}
                            style={{
                              display: "flex", alignItems: "center", gap: 12, minHeight: 48, padding: "10px 14px", cursor: "pointer",
                              border: `1px solid ${checked ? "var(--accent)" : "var(--border)"}`, borderRadius: 8,
                              background: checked ? "var(--accent-soft, var(--surface-2))" : "var(--surface)",
                            }}
                          >
                            <input
                              type="radio"
                              name={it.id}
                              value={value}
                              checked={checked}
                              onChange={() => setAnswers((a) => ({ ...a, [it.id]: value }))}
                              style={{ width: 20, height: 20, margin: 0, accentColor: "var(--accent)" }}
                            />
                            <span className="num" style={{ width: 16, textAlign: "center", fontWeight: 600 }} aria-hidden="true">
                              {value}
                            </span>
                            <span>{label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </fieldset>
                ))}
                {error ? (
                  <Callout tone="crit" title="Não foi possível enviar">
                    {error}
                  </Callout>
                ) : null}
                <div className="vx-stack" style={{ gap: 8 }}>
                  <p className="text-2 num" style={{ margin: 0 }} aria-live="polite">
                    {answered} de {items.length} respondidas
                  </p>
                  <Button type="submit" variant="primary" block loading={busy} disabled={!complete}>
                    Enviar resposta
                  </Button>
                </div>
              </form>
            </>
          ) : null}
        </div>
      </main>
    </div>
  );
}
